// Everything a tester who knows nothing about this demo needs in order to (a) hold up
// their end of the call and (b) tell whether Mira got it right.
//
// The agent reads the account live out of Supabase (project zqicfacynnezpwmmwhbh, the
// fsh_* tables — fsh_customers, fsh_orders_v, fsh_order_items, fsh_returns_v, fsh_kb,
// fsh_kb_tier_policies, fsh_tier_benefits, fsh_inventory_v, fsh_tickets_v). This file is
// the SAME data, restated for the person on the other end of the line — so if a number
// here and a number Mira says disagree, that is a real finding, not a stale fixture. Every
// date below was read off a `_v` view on 2026-08-12, the day this file was written.

export interface TierRow {
  label: string;
  icon: string;
  insider: string;
  member: string;
}

/**
 * The whole demo in one table. Two callers can ask the identical refund question and hear
 * two credible, different answers — and neither is improvised, because every promise is
 * read out of `fsh_tier_benefits` / `fsh_kb_tier_policies`. Mira is forbidden from inventing
 * an entitlement, so anything she offers that is not in this table is a bug. Unlike the
 * fintech crew, NO tier here is ever put through to a live human on the call — every tier's
 * escalation is a callback or an email; only the timing changes.
 */
export const TIER_POLICY: TierRow[] = [
  { label: "Refund release",          icon: "Instant, on the call",                   insider: "2-3 business days after warehouse receipt", member: "5-7 business days after warehouse receipt" },
  { label: "Return window",           icon: "60 days from delivery",                  insider: "45 days from delivery",                     member: "30 days from delivery" },
  { label: "Return pickup",           icon: "Free courier pickup",                    insider: "Free courier pickup",                       member: "Free store drop-off — $5.95 for courier" },
  { label: "Cancellation save offer", icon: "Free express reship, arrives in 2 days", insider: "Free express upgrade on this order",        member: "Switch to store pickup, ready next business day" },
  { label: "Pickup hold",             icon: "7 days from ready-for-pickup",           insider: "5 days from ready-for-pickup",              member: "3 days from ready-for-pickup" },
  { label: "Price-adjustment window", icon: "14 days of purchase",                    insider: "7 days of purchase",                        member: "None on this tier" },
  { label: "Early access",            icon: "7 days before public drop",              insider: "48 hours before public drop",               member: "None — sees drops at public launch" },
  { label: "Express shipping",        icon: "Free, always",                           insider: "Free at $150 or more",                      member: "$12.95" },
  { label: "Stylist service",         icon: "Dedicated stylist, same-day callback",   insider: "Seasonal group styling sessions",           member: "None on this tier" },
  { label: "Escalation follow-up",    icon: "Priority callback within 2 hours",       insider: "Callback same business day",                member: "Email within 24 hours" },
];

export type TierKey = "Icon" | "Insider" | "Member";

export function tierValue(row: TierRow, tier: string): string {
  return tier === "Icon" ? row.icon : tier === "Insider" ? row.insider : row.member;
}

export interface Playbook {
  /** One-line framing of the situation, in the caller's own words. */
  situation: string;
  /** The first thing to say once Mira picks up. */
  opener: string;
  /** Lines to push with AFTER the first answer — these are where tier discipline shows. */
  followUps: string[];
  /** Ground truth Mira should be quoting. If her number differs, that is a finding. */
  facts: { label: string; value: string }[];
  /** What a CORRECT call looks like, in order. */
  expect: string[];
  /** Red flags. If you see one of these, the agent regressed. */
  watchOut: string[];
}

// Rules that apply on EVERY call regardless of who you are, so they are not repeated
// twelve times below. Surfaced in the UI alongside the per-caller list.
export const UNIVERSAL_WATCHOUTS = [
  "Any account detail before you have been verified — not the order, not the tier, not even whether the account exists",
  "The loyalty tier named more than once per call, or used as a greeting or as flattery (\"welcome back, Icon member\")",
  "Comparing your tier to another one, or telling you what Icon or Insider would have got instead",
  "A promise that is not backed by tier policy or tier benefits — no invented entitlement, no restock date pulled from thin air",
  "\"Transferring\", \"connecting you\", or any team name — there is no transfer tool anywhere in this crew, only one continuous Mira",
  "An exclamation mark anywhere, or two things said back to back before you get a chance to answer",
];

export const VERIFICATION_NOTE =
  "Mira asks for your first and last name in one breath, then the email on the account. She reads the local part of the email back letter by letter and waits for a yes before looking anything up. Give a wrong last name on purpose and she must refuse without telling you which field failed — two misses end the call warmly, and there is never a third lookup.";

/** Keyed by the raw fsh_customers.customer_id. */
export const FASHION_PLAYBOOKS: Record<string, Playbook> = {
  // ─── Icon ──────────────────────────────────────────────────────────────────
  "FSH-1001": {
    situation:
      "You returned a silk dress. The warehouse logged it as received yesterday, but the refund has not shown up yet. You were just on chat about it.",
    opener: "I sent a dress back and I can see the warehouse got it — so where's my refund?",
    followUps: [
      "Can you just release that back to my card right now, while I'm on the phone?",
      "It's been almost a week — why did it take that long to even get to the warehouse?",
      "Do all your customers get their money back this fast?",
    ],
    facts: [
      { label: "Return RT-7001", value: "Celeste Silk Midi Dress, Ivory, size M — $248.00" },
      { label: "Order NF-40001", value: "delivered 2026-08-04" },
      { label: "Return received at warehouse", value: "2026-08-11 (yesterday)" },
      { label: "Icon refund entitlement", value: "instant release, on the call" },
    ],
    expect: [
      "Verifies name and email before saying anything about the account, then recognises the open chat ticket in one clause without making you re-explain it",
      "Turn one: where the return physically is and the real received date — no tier, no fix yet",
      "Turn two: names Icon exactly once, attributes the instant release to it, states the real $248.00, and asks \"shall I?\" before doing anything",
      "Confirms the release only after you say yes — never claims it is already back on your card before that",
    ],
    watchOut: [
      "Icon named twice, or used as a greeting (\"welcome back, Icon member\")",
      "The refund described as already released before you have said yes",
      "A vague \"let me see what I can do\" instead of a concrete same-day release",
    ],
  },
  "FSH-1002": {
    situation:
      "Your Runa sneakers came in a size 7 and run small. You told chat you wanted a 7.5 exchanged in and it couldn't book it.",
    opener: "The sneakers I ordered run small — I need a seven and a half instead of the seven I got.",
    followUps: [
      "Do I have to post the old pair back before you send the new size, or can you send it now?",
      "Are you sure a seven and a half is actually in stock at my store?",
    ],
    facts: [
      { label: "Order NF-40002", value: "Runa Knit Sneakers, Cloud White, size 7 — $138.00, delivered 2026-08-09" },
      { label: "Size 7.5 stock", value: "14 online, 5 at your Austin store (ST-01)" },
      { label: "Icon return window", value: "60 days from delivery — well inside it" },
      { label: "Also on file", value: "Tapered Jogger, Black, size M (order NF-40003, delivered 2026-07-13)" },
    ],
    expect: [
      "Confirms the item and the size you want before doing anything, even though the ticket already names 7.5",
      "Checks stock for the 7.5 BEFORE offering the exchange, never after",
      "Offers the exchange over a refund since it's a fit issue and the size is in stock — free courier pickup on the old pair, no fee",
      "Does NOT name Icon for the pickup being free — Insider gets the identical free courier pickup, so this is not a tier moment",
      "Somewhere before goodbye, exactly one styling suggestion tied to the sneakers and jogger you already own, ending in one question",
    ],
    watchOut: [
      "Icon credited for something Insider also gets for free",
      "A size promised before check_stock actually confirms it",
      "A second styling pitch after you say no once",
    ],
  },
  "FSH-1003": {
    situation: "You asked chat to book you a stylist call to plan your fall wardrobe and it couldn't do it.",
    opener: "Chat couldn't book me a stylist call — I want someone to actually help me plan my fall wardrobe.",
    followUps: ["Can they call me today?", "Is this an actual stylist, or just another bot?"],
    facts: [
      { label: "Order NF-40004", value: "Marlowe Trench Coat, Camel, size L — $268.00, delivered 2026-08-01" },
      { label: "Icon stylist perk", value: "dedicated personal stylist, same-day callback" },
      { label: "Lifetime spend", value: "$4,200.00" },
    ],
    expect: [
      "Verifies, recognises the ticket, then routes the styling request through without re-asking what you already told chat",
      "Names Icon exactly once, attributes the same-day callback to it, and offers to book it — \"shall I set that up?\"",
      "Confirms the callback and its window only after the booking tool actually returns ok — never \"you're all set\" before that",
      "States the callback window using the tool's own word (\"today\" / \"same day\"), not an invented time",
    ],
    watchOut: [
      "The callback confirmed as booked before the tool has actually returned",
      "Icon named a second time later in the same call",
    ],
  },
  "FSH-1004": {
    situation:
      "You bought a wrap dress and two days later saw it marked down online. Chat couldn't do anything about the difference.",
    opener: "I bought a dress and a couple of days later I saw it marked down — can I get the difference credited?",
    followUps: [
      "How much are you actually going to give me back?",
      "Why do I have to wait for anything, can't you just do it now?",
    ],
    facts: [
      { label: "Order NF-40005", value: "Selene Wrap Dress, Emerald, size S — $172.00, placed 2026-08-07, delivered 2026-08-10" },
      { label: "Icon price-adjustment window", value: "14 days of purchase, credit issued same day once approved" },
      { label: "Days since purchase today", value: "5 — inside the window" },
      { label: "Sale price on file", value: "none — the system holds no markdown or price-history figure" },
    ],
    expect: [
      "Confirms the item and checks the real purchase date (5 days ago) against the 14-day window rather than guessing",
      "Since no discount figure is stored anywhere, asks YOU to state the new price rather than inventing one",
      "Names Icon once, attributes the same-day credit to it, and offers to issue it — \"shall I?\"",
      "Confirms the credit only once it is actually issued, not as an immediate \"already refunded\"",
    ],
    watchOut: [
      "A specific discount amount stated before you have given one",
      "Icon named a second time, or used to imply Insider gets nothing at all (Insider has a shorter but real 7-day window)",
      "\"Sure, no problem\" without checking the 14-day window against the real purchase date",
    ],
  },

  // ─── Insider ───────────────────────────────────────────────────────────────
  "FSH-1005": {
    situation:
      "Your sneaker order has sat in processing for six days because of a warehouse stock reallocation. You told chat you just want to cancel it. You are mildly frustrated.",
    opener: "My order has been stuck in processing for six days — I just want to cancel it.",
    followUps: [
      "No, I don't want an upgrade, I want it cancelled.",
      "Fine — if I do cancel, when do I actually get my money back?",
      "Why was it even stuck for six days in the first place?",
    ],
    facts: [
      { label: "Order NF-40006", value: "Vela Trail Sneakers, Black, size 10 — $164.00, placed 2026-08-06" },
      { label: "Status", value: "still processing, not shipped — promised 2026-08-14" },
      { label: "Delay reason on file", value: "warehouse stock reallocation" },
      { label: "Insider save lever", value: "courtesy free express upgrade on this order, next express date" },
    ],
    expect: [
      "States plainly why it's stuck (the warehouse stock reallocation) before anything else — no blame on you",
      "Confirms cancelling is genuinely possible because it has not shipped yet",
      "Exactly ONE save attempt before any cancel: offers the free express upgrade, names Insider once, ends \"want that instead, or shall I cancel?\"",
      "The moment you decline the save, lets it go — no second attempt, no guilt trip, straight to cancelling",
      "If it is cancelled, reads back the refund figure the cancellation tool actually returns rather than assuming a number",
    ],
    watchOut: [
      "A second save attempt after you've said cancel once",
      "Guilt, or a repeated pitch, when you push back",
      "\"Transfer\" or a live person offered — no transfer tool exists anywhere in this crew",
    ],
  },
  "FSH-1006": {
    situation: "Your puffer jacket was promised yesterday and still has not turned up. You asked chat why and got no real answer.",
    opener: "My jacket was supposed to arrive yesterday and it's still not here.",
    followUps: [
      "So is it actually coming today or not?",
      "Can you at least upgrade the shipping since it's already late?",
    ],
    facts: [
      { label: "Order NF-40007", value: "Halo Cropped Puffer, Black, size M — $198.00" },
      { label: "Shipped", value: "2026-08-07, USPS — promised 2026-08-11 (yesterday), not yet marked delivered" },
      { label: "Delay reason on file", value: "carrier network delay" },
      { label: "Revised delivery date on file", value: "none — the order record has no updated ETA" },
    ],
    expect: [
      "States the real ship date (2026-08-07, USPS), the delay reason (carrier network delay), and that the promised date (2026-08-11) has passed with no revised ETA on file — never invents a new one",
      "Does NOT offer a tier-based remedy for the plain delay — no such policy exists in the system for this exact situation, on any tier; a free express reship is only ever on offer once you actually ask to cancel",
      "Logs a case and commits to the Insider follow-up — a callback before end of business day — since nothing else can honestly be promised",
    ],
    watchOut: [
      "A free express reship offered just because the order is late — that offer only exists in the system for an actual cancellation request, not a plain delay. Hearing it here means the agent invented an entitlement",
      "Insider named as the reason for a remedy the system does not actually have on file for this topic",
      "A new delivery date invented when the order record has none on file",
    ],
  },
  "FSH-1007": {
    situation: "You ordered hiking boots for pickup and asked chat whether they're ready to collect at the Grand Central Galleria store.",
    opener: "I ordered some boots for pickup — are they actually ready yet?",
    followUps: ["How long will you actually hold them for me?", "What are your hours there?"],
    facts: [
      { label: "Order NF-40008", value: "Ridge Hiking Boot, Brown, size 11 — $176.00" },
      { label: "Ready for pickup since", value: "2026-08-11 (yesterday), Grand Central Galleria (ST-03), Chicago" },
      { label: "Insider pickup hold", value: "5 days from ready date — expires 2026-08-16" },
      { label: "Store hours", value: "10am-9pm weekdays and Saturday, 11am-7pm Sunday" },
    ],
    expect: [
      "Turn one: readiness plus the store name, nothing else — floor handed back",
      "Turn two, only once you ask: the hold date in natural words, address or hours only if you actually ask for them",
      "Names Insider only if you ask how long the hold lasts, attributing the 5 days to it",
    ],
    watchOut: [
      "The address, hours, and hold date all volunteered in one breath before you've asked for any of it",
      "A 7-day Icon hold or a 3-day Member hold quoted instead of your actual 5 days",
    ],
  },
  "FSH-1008": {
    situation: "You asked chat when Insider early access opens for the next seasonal drop and it couldn't give you a straight answer.",
    opener: "When does early access actually open for the next drop?",
    followUps: [
      "So what's the actual date I can start shopping?",
      "Is that the same for everyone, or just people on my plan?",
    ],
    facts: [
      { label: "Insider early-access entitlement", value: "opens 48 hours before public launch" },
      { label: "Specific date for the next drop", value: "not on file anywhere in the system" },
      { label: "Last order", value: "Solstice Cashmere Sweater, Camel, size S — $188.00, delivered 2026-07-26" },
    ],
    expect: [
      "States the 48-hour rule, names Insider once, attributes the window to it",
      "If pressed for an actual calendar date, admits honestly that none is on file yet rather than guessing one",
      "One clean answer, floor handed back, no stacked second question",
    ],
    watchOut: [
      "An actual date invented for \"the next drop\"",
      "Icon's 7-day window quoted instead of your 48 hours",
      "The tiers compared (\"Icon members get a full week\")",
    ],
  },

  // ─── Member ────────────────────────────────────────────────────────────────
  "FSH-1009": {
    situation:
      "You returned a field jacket because you changed your mind. The warehouse has it, but you are adamant chat told you the refund would be instant.",
    opener: "Chat told me my refund would be instant and it still hasn't shown up — where's my money?",
    followUps: [
      "Why would chat just lie to me, then?",
      "I bet other customers get theirs back the same day — why not me?",
      "This is ridiculous, I want it back today.",
    ],
    facts: [
      { label: "Return RT-7009", value: "Harbor Field Jacket, Olive, size L — $189.00" },
      { label: "Received at warehouse", value: "2026-08-11 (yesterday)" },
      { label: "Speed quoted on YOUR OWN return record", value: "5-7 business days — not instant" },
      { label: "Member refund entitlement", value: "5-7 business days after warehouse receipt, expected 2026-08-16 to 2026-08-18" },
    ],
    expect: [
      "Turn one: confirms the jacket and that it's received, with the real date — no tier, no promise yet",
      "Corrects the \"instant\" claim gently in ONE line, using the 5-7 business days already on your own record — never argues twice, never accuses you of lying",
      "Turn two: names Member once, attributes the honest 5-7 business day timeline to it, sets the refund in motion (never \"done\") with the real window",
      "On pushback, holds the line warmly: restates what your plan provides and what's already moving — never names Icon or any other customer",
    ],
    watchOut: [
      "Agreeing that chat did say instant — your own record says otherwise",
      "Icon, or any other tier or customer, named or even hinted at",
      "A same-day release promised just to placate you",
      "An apology for being on the Member tier itself",
    ],
  },
  "FSH-1010": {
    situation: "You ordered sneakers for pickup a while back and asked chat whether they're still being held for you.",
    opener: "I ordered some sneakers for pickup — are they still being held for me?",
    followUps: [
      "Well can you just get me another pair, then?",
      "Nobody told me the hold was about to run out — why not?",
    ],
    facts: [
      { label: "Order NF-40011", value: "Runa Knit Sneakers, Storm Grey, size 8 — $138.00" },
      { label: "Ready for pickup since", value: "2026-08-07, Palm Grove Mall (ST-01), Austin" },
      { label: "Member pickup hold", value: "3 days — expired 2026-08-10 (confirmed expired)" },
      { label: "Current stock for a re-pull", value: "4 pairs, size 8, at ST-01 (11 online)" },
    ],
    expect: [
      "States plainly that the 3-day hold lapsed and the pair went back to the floor — no blame on you",
      "Checks stock BEFORE offering anything, confirming the pair is genuinely still there at your store",
      "Offers a fresh pickup as a NEW hold, restating the 3-day Member window if it comes up again",
    ],
    watchOut: [
      "Any blame for missing the window",
      "A re-pull promised before check_stock actually confirms it",
      "A 7-day Icon or 5-day Insider hold quoted instead of your 3 days",
    ],
  },
  "FSH-1011": {
    situation: "You want to return a pair of jeans you bought about five weeks ago.",
    opener: "I want to return some jeans — I bought them about five weeks ago.",
    followUps: ["It's only a few days over, come on, make an exception.", "Would this be different if I were on a better plan?"],
    facts: [
      { label: "Order NF-40012", value: "Denim Straight Jean, Mid Wash, size L — $118.00" },
      { label: "Delivered", value: "2026-07-08 — 35 days ago" },
      { label: "Member return window", value: "30 days from delivery — you are 5 days past it" },
    ],
    expect: [
      "States the real delivered date and the exact day count (35 days) against the 30-day window, plainly, with no apology for the tier",
      "Holds the line warmly on pushback — never bends the window, never mentions what Icon's 60 days or Insider's 45 days would have covered",
      "Offers a logged case for review as the honest alternative — framed as a review, not a guaranteed yes",
    ],
    watchOut: [
      "The window quietly stretched \"just this once\"",
      "Another tier's window number spoken out loud, even to explain why you're out of luck",
      "The case offered as if it were already an approval",
    ],
  },
  "FSH-1012": {
    situation: "You opened your last order and it was the wrong item. You are upset and told chat as much.",
    opener: "I opened my order and it's not even what I ordered — this is the wrong item.",
    followUps: ["I'm not paying anything to send this back, it's not my fault.", "How fast can you actually get me the right one?"],
    facts: [
      { label: "Order NF-40013", value: "Breeze Poplin Shirt, White, size S — $88.00, delivered 2026-08-10" },
      { label: "What actually arrived", value: "not recorded anywhere — the system only holds what was ordered, so Mira must ask you, not assume" },
      { label: "Member courier fee, normally", value: "$5.95 — waived on every tier for a wrong item" },
    ],
    expect: [
      "One clarifying question at most about what actually turned up — never an interrogation",
      "One specific apology, not a generic one, then straight to fixing it",
      "Free courier pickup offered despite the usual Member fee — waived because the reason is wrong_item, on every tier",
      "Checks stock before promising a replacement; a return if the right size or item isn't there",
      "Logs a case with the Member follow-up (email within 24 hours), since this is also a complaint",
    ],
    watchOut: [
      "The $5.95 Member courier fee charged anyway",
      "More than one clarifying question, drifting into an interrogation about fault",
      "The apology repeated, or generic instead of specific",
    ],
  },
};

export function getPlaybook(customerId: string): Playbook | undefined {
  return FASHION_PLAYBOOKS[customerId];
}
